import React, { type ReactNode, useCallback } from 'react';
import type {
  TrackingContext,
  TrackingPlacement,
} from '@mentimeter/http-clients';
import type { WorkspaceCollaborationEntitlement } from '@mentimeter/entitlements/types';
import { EntitlementPaywallTrigger } from '../../lib/paywall/PaywallTrigger';
import type { PaywallTriggerHandler } from '../../lib/paywall/PaywallTrigger';
import { UpgradeType } from '../../types';
import type { PaywallSeriesTrackingProperties } from '../../types';
import { useOpenPopoverPaywall } from '../../lib/hooks/use-popover-paywall';
import { RequestUpgradePopoverGeneric } from '../../lib/request-upgrade/RequestUpgradePopoverGeneric';
import { SelfUpgradePopoverGeneric } from '../../lib/self-upgrade/SelfUpgradePopoverGeneric';

interface CommentOnlyPaywallDataHandlerProps {
  entitlement: WorkspaceCollaborationEntitlement;
  children: ReactNode;
  trackingProperties?: PaywallSeriesTrackingProperties | undefined;
  trackingContext: TrackingContext;
  trackingPlacement: TrackingPlacement;
  isEducationalUser: boolean;
}

export function CommentOnlyPaywallDataHandler({
  entitlement,
  children,
  trackingContext,
  trackingPlacement,
  trackingProperties,
  isEducationalUser,
}: CommentOnlyPaywallDataHandlerProps) {
  const openPopover = useOpenPopoverPaywall();

  const handleTrigger = useCallback<PaywallTriggerHandler>(
    async ({ triggerRef, upgradeType }) => {
      if (upgradeType === UpgradeType.REQUEST_UPGRADE) {
        openPopover(
          <RequestUpgradePopoverGeneric
            title="Unlock unlimited collaboration with Pro"
            description="To edit and present this Menti, your role needs to be upgraded. "
            trackingContext={trackingContext}
            trackingPlacement={trackingPlacement}
          />,
          triggerRef,
        );
      } else {
        openPopover(
          <SelfUpgradePopoverGeneric
            isEducationalUser={isEducationalUser}
            title="Unlock unlimited collaboration with Pro"
            description="You’re on Free. To co-edit and co-present workspace Mentis, you need to upgrade to Pro ."
            upgradeEvent="Plans from editor"
            upgradeEventContext="Comment only"
            trackingContext={trackingContext}
            trackingPlacement={trackingPlacement}
            trackingProperties={trackingProperties}
          />,
          triggerRef,
        );
      }
    },
    [
      openPopover,
      trackingContext,
      trackingPlacement,
      trackingProperties,
      isEducationalUser,
    ],
  );

  return (
    <EntitlementPaywallTrigger
      entitlement={entitlement}
      onTrigger={handleTrigger}
    >
      {children}
    </EntitlementPaywallTrigger>
  );
}
